import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import styled, { keyframes } from "styled-components";
import { getAppConfig } from "../../appConfig";
import { api } from "../api/api";

/*
 * Support chatbot widget.
 *
 * Floating bubble bottom-right; tap to open a chat panel that streams
 * answers from /api/v1/support/chat (Symfony SupportChatController).
 * The backend grounds responses in the wiki corpus and offers a human
 * handoff when it doesn't know — we detect that phrase and surface a
 * CTA instead of relying on the user to copy/paste.
 */

const HANDOFF_PHRASE = "connect you with our support team";

// What the assistant is doing while it has nothing to say yet. An answer that
// looks things up takes the better part of a minute, and a bare "…" for that
// long reads as a hang. The server names the tool it is running.
const TOOL_STATUS = {
  search_ball_catalog: "Searching the ball catalog…",
  get_low_stock: "Checking your stock…",
  search_products: "Looking through your products…",
  get_sales_summary: "Adding up your sales…",
  list_suppliers: "Looking up your suppliers…",
  preview_reorder_points: "Working out reorder points…",
  apply_reorder_points: "Saving reorder points…",
  preview_purchase_order: "Drafting the purchase order…",
  create_purchase_order: "Creating the purchase order…",
};

// The server writes at least every few seconds while a turn is running, so a
// stream this quiet is one that died without saying so.
const STALL_MS = 60000;
// A lost stream is not a lost answer: the server finishes the turn and keeps
// the answer for the widget to come back for.
const RECOVER_EVERY_MS = 2000;
const RECOVER_FOR_MS = 120000;
const RECOVER_MISSES = 3;

const TROUBLE =
  "Sorry — I'm having trouble right now. Please try again in a moment, or contact support directly if it persists.";
const UNREACHABLE = "Sorry — I couldn't reach support right now. Please check your connection and try again.";
const CUT_SHORT = "The connection dropped before the answer finished.";

// Phones, either way up. A floating card has no room there: the panel is the
// screen.
const PHONE = "(max-width: 600px), (max-height: 500px)";

const slideUp = keyframes`
  from { transform: translateY(16px); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.45; }
`;

const Bubble = styled.button`
  position: fixed;
  right: calc(16px + env(safe-area-inset-right, 0px));
  bottom: calc(80px + env(safe-area-inset-bottom, 0px));
  z-index: 2000;

  @media (orientation: landscape) {
    bottom: calc(16px + env(safe-area-inset-bottom, 0px));
  }
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: none;
  background: ${(p) => p.theme.color.primary};
  color: #fff;
  font-size: 26px;
  line-height: 1;
  box-shadow: 0 8px 24px rgba(0, 24, 126, 0.35);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  -webkit-tap-highlight-color: transparent;

  &:active { transform: scale(0.96); }
  @media (pointer: fine) { &:hover { background: ${(p) => p.theme.color.primaryDark}; } }
`;

const Panel = styled.div`
  position: fixed;
  z-index: 2001;
  right: calc(16px + env(safe-area-inset-right, 0px));
  bottom: calc(80px + env(safe-area-inset-bottom, 0px));

  @media (orientation: landscape) {
    bottom: calc(16px + env(safe-area-inset-bottom, 0px));
  }
  width: 380px;
  max-width: calc(100vw - 24px);
  height: 560px;
  max-height: calc(100vh - 24px);
  background: ${(p) => p.theme.color.surface};
  border: 1px solid ${(p) => p.theme.color.border};
  border-radius: ${(p) => p.theme.radius.lg};
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-family: ${(p) => p.theme.font.family};
  animation: ${slideUp} 160ms ease-out;

  @media ${PHONE} {
    /* The screen here is what is left once the browser's bars and the keyboard
       have taken their share. 100vh is not that -- it measures the screen with
       the bars hidden -- and it pushed the header, and with it the only way to
       close the chat, up behind the address bar. --vv-* follow the visual
       viewport (useVisibleArea). */
    top: var(--vv-top, 0px);
    right: 0;
    bottom: auto;
    left: 0;
    width: auto;
    max-width: none;
    height: var(--vv-height, 100%);
    max-height: none;
    border: none;
    border-radius: 0;
    box-shadow: none;
  }
`;

const Header = styled.div`
  background: ${(p) => p.theme.color.primary};
  color: #fff;
  padding: ${(p) => p.theme.spacing.sm} ${(p) => p.theme.spacing.sm} ${(p) => p.theme.spacing.sm}
    ${(p) => p.theme.spacing.md};
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: ${(p) => p.theme.font.weightSemibold};
  flex-shrink: 0;

  @media ${PHONE} {
    font-size: ${(p) => p.theme.font.sizeLg};
    padding-top: calc(${(p) => p.theme.spacing.sm} + env(safe-area-inset-top, 0px));
    padding-right: calc(${(p) => p.theme.spacing.sm} + env(safe-area-inset-right, 0px));
    padding-left: calc(${(p) => p.theme.spacing.md} + env(safe-area-inset-left, 0px));
  }
`;

const CloseBtn = styled.button`
  background: transparent;
  color: #fff;
  border: none;
  font-size: 26px;
  line-height: 1;
  width: 44px;
  height: 44px;
  border-radius: ${(p) => p.theme.radius.sm};
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  &:active { background: rgba(255, 255, 255, 0.15); }
`;

const Messages = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  padding: ${(p) => p.theme.spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.theme.spacing.sm};
  background: ${(p) => p.theme.color.surfaceAlt};

  @media ${PHONE} {
    padding-right: calc(${(p) => p.theme.spacing.md} + env(safe-area-inset-right, 0px));
    padding-left: calc(${(p) => p.theme.spacing.md} + env(safe-area-inset-left, 0px));
  }
`;

const Bubble2 = styled.div`
  max-width: 85%;
  padding: ${(p) => p.theme.spacing.sm} ${(p) => p.theme.spacing.md};
  border-radius: ${(p) => p.theme.radius.lg};
  font-size: ${(p) => p.theme.font.sizeMd};
  line-height: 1.45;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-wrap: anywhere;

  @media ${PHONE} {
    max-width: 90%;
    font-size: ${(p) => p.theme.font.sizeLg};
  }

  ${(p) =>
    p.$from === "user"
      ? `
    align-self: flex-end;
    background: ${p.theme.color.primary};
    color: #fff;
    border-bottom-right-radius: ${p.theme.radius.sm};
  `
      : `
    align-self: flex-start;
    background: ${p.theme.color.surface};
    color: ${p.$waiting ? p.theme.color.textMuted : p.theme.color.text};
    border: 1px solid ${p.theme.color.border};
    border-bottom-left-radius: ${p.theme.radius.sm};
  `}
`;

const Working = styled.span`
  animation: ${pulse} 1.4s ease-in-out infinite;
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

const Status = styled.div`
  align-self: flex-start;
  padding: 0 ${(p) => p.theme.spacing.xs};
  color: ${(p) => p.theme.color.textMuted};
  font-size: ${(p) => p.theme.font.sizeSm};

  @media ${PHONE} { font-size: ${(p) => p.theme.font.sizeMd}; }
`;

const Failure = styled.div`
  align-self: flex-start;
  max-width: 90%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${(p) => p.theme.spacing.sm};
  padding: ${(p) => p.theme.spacing.sm} ${(p) => p.theme.spacing.md};
  border: 1px solid ${(p) => p.theme.color.error};
  border-radius: ${(p) => p.theme.radius.lg};
  background: ${(p) => p.theme.color.errorBg};
  color: ${(p) => p.theme.color.text};
  font-size: ${(p) => p.theme.font.sizeMd};
  line-height: 1.45;

  @media ${PHONE} { font-size: ${(p) => p.theme.font.sizeLg}; }
`;

const RetryBtn = styled.button`
  min-height: 44px;
  padding: 0 ${(p) => p.theme.spacing.md};
  border: 1px solid ${(p) => p.theme.color.primary};
  border-radius: ${(p) => p.theme.radius.md};
  background: ${(p) => p.theme.color.surface};
  color: ${(p) => p.theme.color.primary};
  font-family: ${(p) => p.theme.font.family};
  font-size: inherit;
  font-weight: ${(p) => p.theme.font.weightSemibold};
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  &:active { transform: scale(0.98); }
`;

const SourceLink = styled.a`
  color: ${(p) => p.theme.color.primary};
  font-weight: ${(p) => p.theme.font.weightSemibold};
  text-decoration: underline;
  &:active { opacity: 0.7; }
`;

// Light Markdown renderer for the chat bubble: promotes **bold** to <strong>
// and [text](url) to real anchors. Not a full parser — just the two patterns
// the model is instructed to use. Newlines are preserved by the bubble's
// white-space: pre-wrap, so plain/numbered/dashed lists render as written.
function renderWithLinks(text) {
  if (typeof text !== "string" || text === "") return text;
  const re = /\*\*([^*\n]+)\*\*|\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)/g;
  const parts = [];
  let last = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    if (m[1] !== undefined) {
      parts.push(<strong key={`b-${m.index}`}>{m[1]}</strong>);
    } else {
      parts.push(
        <SourceLink
          key={`l-${m.index}-${m[3]}`}
          href={m[3]}
          target="_blank"
          rel="noopener noreferrer"
        >
          {m[2]}
        </SourceLink>
      );
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

const HandoffCTA = styled.button`
  align-self: flex-start;
  background: ${(p) => p.theme.color.accent};
  color: ${(p) => p.theme.color.primaryDark};
  border: none;
  padding: ${(p) => p.theme.spacing.sm} ${(p) => p.theme.spacing.md};
  border-radius: ${(p) => p.theme.radius.md};
  font-weight: ${(p) => p.theme.font.weightSemibold};
  font-size: ${(p) => p.theme.font.sizeMd};
  cursor: pointer;
  margin-top: ${(p) => p.theme.spacing.xs};
  &:active { transform: scale(0.98); }

  @media ${PHONE} {
    min-height: 44px;
    font-size: ${(p) => p.theme.font.sizeLg};
  }
`;

const InputBar = styled.form`
  display: flex;
  gap: ${(p) => p.theme.spacing.sm};
  padding: ${(p) => p.theme.spacing.sm};
  border-top: 1px solid ${(p) => p.theme.color.border};
  background: ${(p) => p.theme.color.surface};
  flex-shrink: 0;

  @media ${PHONE} {
    padding-right: calc(${(p) => p.theme.spacing.sm} + env(safe-area-inset-right, 0px));
    padding-bottom: calc(${(p) => p.theme.spacing.sm} + env(safe-area-inset-bottom, 0px));
    padding-left: calc(${(p) => p.theme.spacing.sm} + env(safe-area-inset-left, 0px));

    /* The keyboard covers the home indicator; leaving room for it would open
       a gap between the keyboard and the input. */
    [data-keyboard="open"] & { padding-bottom: ${(p) => p.theme.spacing.sm}; }
  }
`;

const TextInput = styled.input`
  flex: 1;
  min-width: 0;
  border: 1px solid ${(p) => p.theme.color.border};
  border-radius: ${(p) => p.theme.radius.md};
  padding: 12px ${(p) => p.theme.spacing.md};
  font-size: ${(p) => p.theme.font.sizeMd};
  font-family: ${(p) => p.theme.font.family};
  color: ${(p) => p.theme.color.text};
  background: ${(p) => p.theme.color.surface};
  outline: none;
  &:focus { border-color: ${(p) => p.theme.color.primary}; }
  &:disabled { background: ${(p) => p.theme.color.surfaceAlt}; }

  @media ${PHONE} { font-size: ${(p) => p.theme.font.sizeLg}; }
`;

const SendBtn = styled.button`
  background: ${(p) => p.theme.color.primary};
  color: #fff;
  border: none;
  border-radius: ${(p) => p.theme.radius.md};
  padding: 0 ${(p) => p.theme.spacing.lg};
  font-family: ${(p) => p.theme.font.family};
  font-weight: ${(p) => p.theme.font.weightSemibold};
  font-size: ${(p) => p.theme.font.sizeMd};
  cursor: pointer;
  min-width: 64px;
  min-height: 44px;
  -webkit-tap-highlight-color: transparent;
  /* Dimmed, not greyed out: white on the border grey could not be read. */
  &:disabled { opacity: 0.4; cursor: not-allowed; }

  @media ${PHONE} { font-size: ${(p) => p.theme.font.sizeLg}; }
`;

const Empty = styled.div`
  margin: auto;
  max-width: 280px;
  padding: ${(p) => p.theme.spacing.md};
  text-align: center;
  color: ${(p) => p.theme.color.textMuted};
  font-size: ${(p) => p.theme.font.sizeMd};
  line-height: 1.5;

  @media ${PHONE} { font-size: ${(p) => p.theme.font.sizeLg}; }
`;

const EmptyTitle = styled.div`
  margin-bottom: ${(p) => p.theme.spacing.xs};
  color: ${(p) => p.theme.color.primary};
  font-size: ${(p) => p.theme.font.sizeXl};
  font-weight: ${(p) => p.theme.font.weightSemibold};
`;

// Compact the selected bowler (from Redux) into the context the support
// backend uses to personalize ball recommendations. Kept small: profile +
// arsenal essentials, no PII beyond the name.
function buildBowlerContext(b) {
  if (!b || typeof b !== "object") return null;
  const name = [b.firstname, b.lastname].filter(Boolean).join(" ").trim();
  const arsenal = Array.isArray(b.balls_drilled)
    ? b.balls_drilled
        .filter((x) => x && (x.enable === undefined || x.enable))
        .slice(0, 30)
        .map((x) => ({
          manufacturer: x.manufacturer || undefined,
          name: x.ball_name || undefined,
          cover: x.cover || undefined,
          rg: x.rg || undefined,
          diff: x.diff || undefined,
          layout: x.layout || x.layout_type || undefined,
        }))
    : [];
  const ctx = {
    name: name || undefined,
    hand: b.hand || undefined,
    style: b.style || undefined,
    rev_rate: b.rev_rate || undefined,
    ball_speed: b.ball_speed || undefined,
    pap:
      b.pap_h || b.pap_v
        ? `${b.pap_h || "?"} x ${b.pap_v || "?"}${b.axis_tilt ? `, tilt ${b.axis_tilt}` : ""}${b.axis_rot ? `, rot ${b.axis_rot}` : ""}`
        : undefined,
    arsenal: arsenal.length ? arsenal : undefined,
  };
  // Drop if nothing useful beyond an empty object.
  return Object.values(ctx).some((v) => v !== undefined) ? ctx : null;
}

// The part of the screen that can actually be seen. CSS has no unit for it:
// the keyboard shrinks the visual viewport and nothing else, and on iOS it
// also slides the page up underneath a fixed panel.
function useVisibleArea(active) {
  const [area, setArea] = useState(null);

  useEffect(() => {
    const vv = typeof window !== "undefined" ? window.visualViewport : null;
    if (!active || !vv) return undefined;

    const measure = () =>
      setArea((was) => {
        const now = {
          top: vv.offsetTop,
          height: vv.height,
          keyboard: window.innerHeight - vv.height > 120,
        };
        return was && was.top === now.top && was.height === now.height && was.keyboard === now.keyboard
          ? was
          : now;
      });

    measure();
    vv.addEventListener("resize", measure);
    vv.addEventListener("scroll", measure);
    return () => {
      vv.removeEventListener("resize", measure);
      vv.removeEventListener("scroll", measure);
    };
  }, [active]);

  return active ? area : null;
}

// The id the server files this turn's answer under.
function newTurnId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function readWithin(reader, ms) {
  let timer;
  const stalled = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error("stalled")), ms);
  });
  return Promise.race([reader.read(), stalled]).finally(() => clearTimeout(timer));
}

// Ask what became of a turn whose stream was lost, until the server has an
// answer for it. Returns { status: "done" | "failed", text }, or null when
// there is nothing to wait for.
async function recoverTurn(url, signal) {
  const until = Date.now() + RECOVER_FOR_MS;
  let misses = 0;
  while (Date.now() < until && !signal.aborted) {
    try {
      const res = await fetch(url, { headers: { Accept: "application/json" }, signal });
      if (res.ok) {
        const turn = await res.json();
        if (turn && turn.status !== "running") return turn;
        misses = 0;
      } else if (res.status === 401) {
        return null;
      } else if (++misses >= RECOVER_MISSES) {
        // The question never reached the server, or this server keeps no turns.
        return null;
      }
    } catch (err) {
      if (err.name === "AbortError") return null;
      /* still offline: that is exactly what we are waiting out */
    }
    await sleep(RECOVER_EVERY_MS);
  }
  return null;
}

// What goes back to the model as the conversation so far: what was asked and
// what was answered. Our own notices and answers that never arrived are not
// part of it.
const forModel = (messages) =>
  messages
    .filter((m) => !m.local && !m.failed && m.content)
    .map(({ role, content }) => ({ role, content }));

const prefersKeyboard = () =>
  typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(pointer: fine)").matches;

function SupportWidget({ accessData, selectedBowler }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [status, setStatus] = useState(null);
  const messagesRef = useRef(null);
  const abortRef = useRef(null);
  const area = useVisibleArea(open);

  const accessToken = accessData?.access_token?.access_token || accessData?.access_token || null;
  const cfg = getAppConfig(typeof window !== "undefined" ? window.location.hostname : "");
  const endpoint = `${cfg.apiServer}v1/support/chat`;

  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messages, open, status, area]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const patchLast = (patch) =>
    setMessages((m) => {
      const last = m[m.length - 1];
      if (!last || last.role !== "assistant") return m;
      return [...m.slice(0, -1), { ...last, ...(typeof patch === "function" ? patch(last) : patch) }];
    });

  // `before` is the conversation the question is added to: all of it, unless a
  // failed question is being asked again in the place where it failed.
  const send = async (text, before = messages) => {
    const question = text.trim();
    if (!question || streaming) return;
    if (!accessToken) {
      setMessages((m) => [...m, { role: "assistant", content: "Please sign in to use chat support.", local: true }]);
      return;
    }
    const nextMessages = [...before, { role: "user", content: question }];
    setMessages([...nextMessages, { role: "assistant", content: "" }]);
    setInput("");
    setStreaming(true);
    setStatus(null);

    const controller = new AbortController();
    abortRef.current = controller;

    const bowlerContext = buildBowlerContext(selectedBowler);
    const turnId = newTurnId();
    let token = accessToken;
    // Whether we know how the turn ended. A stream that just stops does not
    // tell us: the server may well have finished the answer.
    let settled = false;
    let reader = null;

    try {
      // Ask for a fresh token rather than the one in state: this widget streams
      // its own fetch and so never passes through the refresh every other call
      // does. With several tabs open, another one may have rotated it already.
      try {
        token = (await api.ensureAccessToken()) || accessToken;
      } catch {
        /* keep the one we have; the 401 below says what happened */
      }

      const res = await fetch(`${endpoint}?access_token=${encodeURIComponent(token)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
        body: JSON.stringify({
          messages: forModel(nextMessages),
          turn_id: turnId,
          ...(bowlerContext ? { bowler_context: bowlerContext } : {}),
        }),
        signal: controller.signal,
      });
      if (res.status === 401) {
        settled = true;
        patchLast({
          content: "Your session has expired. Reload the page and sign in again — your question will still be here.",
          local: true,
        });
        return;
      }
      if (res.status === 429) {
        // The spending backstop. The server says why in words meant to be read.
        settled = true;
        const refusal = await res.json().catch(() => null);
        patchLast({
          content: refusal?.message || "Chat support is unavailable for this account right now.",
          local: true,
        });
        return;
      }
      if (!res.ok || !res.body) {
        const errText = await res.text().catch(() => "");
        throw new Error(errText || `HTTP ${res.status}`);
      }
      reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await readWithin(reader, STALL_MS);
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf("\n\n")) !== -1) {
          const evt = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          for (const line of evt.split("\n")) {
            if (!line.startsWith("data: ")) continue;
            const payload = line.slice(6);
            try {
              const obj = JSON.parse(payload);
              if (obj.text) {
                setStatus(null);
                patchLast((last) => ({ content: last.content + obj.text }));
              } else if (obj.tool) {
                setStatus(TOOL_STATUS[obj.tool] || "Looking that up…");
              } else if (obj.status === "thinking") {
                setStatus("Thinking…");
              } else if (obj.done) {
                settled = true;
              } else if (obj.error) {
                console.error("[SupportWidget] server error:", obj.error);
                settled = true;
                patchLast({ failed: TROUBLE });
              }
            } catch {
              /* ignore malformed line */
            }
          }
        }
      }
    } catch (err) {
      if (controller.signal.aborted) {
        settled = true;
      } else {
        console.error("[SupportWidget] stream lost:", err);
        // A stream that stalled is still open. Let go of it.
        reader?.cancel().catch(() => {});
      }
    } finally {
      if (!settled) {
        // The phone locked, the app went to the background, the network
        // changed. The question was asked all the same: go and get the answer.
        setStatus("Reconnecting…");
        const turn = await recoverTurn(
          `${endpoint}/turn/${turnId}?access_token=${encodeURIComponent(token)}`,
          controller.signal
        );
        if (!controller.signal.aborted) {
          patchLast((last) => {
            const content = turn?.text && turn.text.length > last.content.length ? turn.text : last.content;
            if (turn?.status === "done") return { content };
            return { content, failed: turn?.status === "failed" ? TROUBLE : content ? CUT_SHORT : UNREACHABLE };
          });
        }
      }
      setStatus(null);
      setStreaming(false);
      abortRef.current = null;
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    send(input);
  };

  const retry = () => {
    const at = messages.map((m) => m.role).lastIndexOf("user");
    if (at !== -1) send(messages[at].content, messages.slice(0, at));
  };

  const requestHandoff = () => {
    // TODO: wire to a real ticket-creation endpoint reusing the admin
    // panel's email flow. For now we just acknowledge.
    setMessages((m) => [
      ...m,
      { role: "assistant", content: "Got it — I've flagged this for the Spectre support team. Someone will reach out by email shortly." },
    ]);
  };

  const lastAssistant = [...messages].reverse().find((m) => m.role === "assistant" && !streaming);
  const showHandoff =
    !streaming &&
    lastAssistant &&
    typeof lastAssistant.content === "string" &&
    lastAssistant.content.toLowerCase().includes(HANDOFF_PHRASE);

  if (!open) {
    return (
      <Bubble type="button" aria-label="Open support chat" onClick={() => setOpen(true)}>
        💬
      </Bubble>
    );
  }

  return (
    <Panel
      role="dialog"
      aria-label="Support chat"
      data-keyboard={area?.keyboard ? "open" : undefined}
      style={area ? { "--vv-top": `${area.top}px`, "--vv-height": `${area.height}px` } : undefined}
    >
      <Header>
        <span>Spectre Support</span>
        <CloseBtn type="button" aria-label="Close" onClick={() => setOpen(false)}>
          ×
        </CloseBtn>
      </Header>
      <Messages ref={messagesRef}>
        {messages.length === 0 && (
          <Empty>
            <EmptyTitle>How can we help?</EmptyTitle>
            Ask anything about Spectre Cloud, POS, or your storefront.
          </Empty>
        )}
        {messages.map((m, i) => {
          const isLast = i === messages.length - 1;
          const waiting = streaming && isLast && m.role === "assistant";
          return (
            <React.Fragment key={i}>
              {(m.content || waiting) && (
                <Bubble2 $from={m.role} $waiting={waiting && !m.content}>
                  {m.content ? renderWithLinks(m.content) : <Working>{status || "…"}</Working>}
                </Bubble2>
              )}
              {waiting && m.content && status && (
                <Status>
                  <Working>{status}</Working>
                </Status>
              )}
              {m.failed && (
                <Failure role="alert">
                  <span>{m.failed}</span>
                  {isLast && !streaming && (
                    <RetryBtn type="button" onClick={retry}>
                      Try again
                    </RetryBtn>
                  )}
                </Failure>
              )}
            </React.Fragment>
          );
        })}
        {showHandoff && (
          <HandoffCTA type="button" onClick={requestHandoff}>
            Yes, connect me with support
          </HandoffCTA>
        )}
      </Messages>
      <InputBar onSubmit={onSubmit}>
        <TextInput
          type="text"
          placeholder="Type your question…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={streaming}
          enterKeyHint="send"
          // On a touch screen focus brings the keyboard up over half the panel
          // before there is anything to answer.
          autoFocus={prefersKeyboard()}
        />
        <SendBtn type="submit" disabled={streaming || !input.trim()}>
          {streaming ? "…" : "Send"}
        </SendBtn>
      </InputBar>
    </Panel>
  );
}

export default connect((state) => ({
  accessData: state.userSession?.accessData,
  selectedBowler: state.bowlers?.selectedCustomer,
}))(SupportWidget);
